import React, { useRef } from 'react';
import { Box } from '@ublend-npm/aulaui-core';
import { Swiper, SwiperSlide } from 'swiper/react';
import type { Swiper as SwiperType } from 'swiper';
import { Mousewheel } from 'swiper/modules';
import SpaceCard from './SpaceCard';
import SpaceCardSkeleton from './SpaceCardSkeleton';
import {
  SPACE_CARD_GAP_PX,
  SPACE_CARD_HEIGHT,
  SPACE_CARD_SKELETON_SURFACE,
  SPACE_CARD_WIDTH,
} from './spaceWidgetConstants';
import { SpaceWidgetCardData } from './SpaceWidget.types';
import SpaceWidgetError, { SpaceWidgetErrorVariant } from './SpaceWidgetError';
import { useSpaceWidgetCarouselWheel } from './useSpaceWidgetCarouselWheel';

import 'swiper/css';

type SpaceWidgetCarouselProps = {
  spaces: SpaceWidgetCardData[];
  showErrorCard?: boolean;
  errorVariant?: SpaceWidgetErrorVariant;
  onErrorRetry?: () => void;
  errorRetrying?: boolean;
  trailingSkeletonCount?: number;
  onSwiper: (swiper: SwiperType) => void;
  onSlideChange: (swiper: SwiperType) => void;
  onReachEnd: () => void;
  onTransitionEnd?: (swiper: SwiperType) => void;
};

const spaceCardSlideSx = {
  width: SPACE_CARD_WIDTH,
  maxWidth: SPACE_CARD_WIDTH,
  flexShrink: 0,
};

const SpaceWidgetCarousel = ({
  spaces,
  showErrorCard = false,
  errorVariant = 'api',
  onErrorRetry,
  errorRetrying = false,
  trailingSkeletonCount = 0,
  onSwiper,
  onSlideChange,
  onReachEnd,
  onTransitionEnd,
}: SpaceWidgetCarouselProps) => {
  const containerRef = useRef<HTMLElement>(null);

  useSpaceWidgetCarouselWheel(containerRef);

  return (
    <Box
      ref={containerRef}
      component="section"
      aria-label="Your spaces"
      aria-busy={trailingSkeletonCount > 0}
      sx={{
        minWidth: 0,
        maxWidth: '100%',
        width: '100%',
        overflow: 'hidden',
        minHeight: { lg: SPACE_CARD_HEIGHT },
        overscrollBehaviorX: 'contain',
        '& .swiper': {
          width: '100%',
          overflow: 'hidden',
          overscrollBehaviorX: 'contain',
        },
        '& .swiper-wrapper': {
          alignItems: 'stretch',
        },
        '& .swiper-slide': {
          width: 'auto',
          flexShrink: 0,
          height: 'auto',
          boxSizing: 'border-box',
        },
      }}
    >
      <Swiper
        modules={[Mousewheel]}
        direction="horizontal"
        slidesPerView="auto"
        spaceBetween={SPACE_CARD_GAP_PX}
        watchOverflow
        watchSlidesProgress
        roundLengths
        resistance
        resistanceRatio={0}
        grabCursor
        mousewheel={{
          forceToAxis: true,
          releaseOnEdges: false,
          sensitivity: 1,
          thresholdDelta: 5,
        }}
        onSwiper={onSwiper}
        onSlideChange={onSlideChange}
        onReachEnd={onReachEnd}
        onTransitionEnd={onTransitionEnd}
      >
        {spaces.map((space) => (
          <SwiperSlide key={space.id} style={{ width: SPACE_CARD_WIDTH }}>
            <Box role="listitem" sx={spaceCardSlideSx}>
              <SpaceCard space={space} tabIndex={0} />
            </Box>
          </SwiperSlide>
        ))}

        {trailingSkeletonCount > 0
          ? Array.from({ length: trailingSkeletonCount }, (_, index) => (
              <SwiperSlide
                key={`space-card-skeleton-${index}`}
                style={{ width: SPACE_CARD_WIDTH }}
              >
                <Box role="listitem" sx={spaceCardSlideSx}>
                  <SpaceCardSkeleton
                    skeletonSurface={SPACE_CARD_SKELETON_SURFACE}
                  />
                </Box>
              </SwiperSlide>
            ))
          : null}

        {showErrorCard && onErrorRetry ? (
          <SwiperSlide style={{ width: SPACE_CARD_WIDTH }}>
            <Box role="listitem" sx={spaceCardSlideSx}>
              <SpaceWidgetError
                variant={errorVariant}
                onRetry={onErrorRetry}
                isRetrying={errorRetrying}
              />
            </Box>
          </SwiperSlide>
        ) : null}
      </Swiper>
    </Box>
  );
};

export default SpaceWidgetCarousel;
